import { useState, useEffect, useRef, useCallback } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { $getRoot, $createParagraphNode, $createTextNode, type EditorState, type LexicalEditor } from 'lexical';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import type { BlogPost } from '@/hooks/useBlogPosts';
import { compressImageToDataUrl } from '@/lib/compressImage';
import ToolbarPlugin from './ToolbarPlugin';

// Empty theme — styling comes from the shared .blog-content CSS class,
// so the editor and the published preview render identically.
const theme = {};

function onError(error: Error) {
  console.error('Lexical error:', error);
}

interface PostEditorProps {
  post: BlogPost | null;
  onSave: (data: {
    title: string;
    slug: string;
    excerpt: string;
    coverImage: string;
    content: string;
    category: string;
    tags: string[];
    status: 'draft' | 'published';
    publishedAt: string;
  }) => void;
  onPreview: (slug: string) => void;
}

// Convert a stored ISO date string to the value format expected by a
// `datetime-local` input (local time, no seconds, no timezone suffix).
function toLocalInput(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Convert a `datetime-local` value back to a stored ISO date string.
function fromLocalInput(value: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
}

// Combine a date-only value with an optional time into the same local
// `YYYY-MM-DDTHH:mm` shape a datetime-local input uses. When no time is
// supplied we default to midday, so several posts can share the same day.
function combineDateAndTime(dateValue: string, timeValue: string): string {
  if (!dateValue) return '';
  return `${dateValue}T${timeValue || '12:00'}`;
}

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
    .replace(/^-|-$/g, '');
}

function UpdateEditorPlugin({ initialHtml, postId }: { initialHtml: string; postId: string | undefined }) {
  const [editor] = useLexicalComposerContext();
  const lastPostIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (lastPostIdRef.current === postId) return;
    lastPostIdRef.current = postId;

    if (!initialHtml) return;

    editor.update(() => {
      const parser = new DOMParser();
      const dom = parser.parseFromString(initialHtml, 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);
      const root = $getRoot();
      root.clear();
      if (nodes.length > 0) {
        nodes.forEach((node) => root.append(node));
      } else {
        root.append($createParagraphNode().append($createTextNode('')));
      }
    });
  }, [editor, initialHtml, postId]);

  return null;
}

export default function PostEditor({ post, onSave, onPreview }: PostEditorProps) {
  const [title, setTitle] = useState(post?.title || '');
  const [slug, setSlug] = useState(post?.slug || '');
  const [excerpt, setExcerpt] = useState(post?.excerpt || '');
  const [coverImage, setCoverImage] = useState(post?.coverImage || '');
  const [category, setCategory] = useState(post?.category || '');
  const [tagsInput, setTagsInput] = useState(post?.tags?.join(', ') || '');
  const [status, setStatus] = useState<'draft' | 'published'>(post?.status || 'draft');
  const [publishedAt, setPublishedAt] = useState<string>(
    post?.publishedAt ? toLocalInput(post.publishedAt) : toLocalInput(new Date().toISOString()),
  );
  const [dateMode, setDateMode] = useState<'datetime' | 'date'>('datetime');
  const [editorHtml, setEditorHtml] = useState(post?.content || '');
  const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);

  // Sync all form state when the selected post changes
  useEffect(() => {
    setTitle(post?.title || '');
    setSlug(post?.slug || '');
    setExcerpt(post?.excerpt || '');
    setCoverImage(post?.coverImage || '');
    setCategory(post?.category || '');
    setTagsInput(post?.tags?.join(', ') || '');
    setStatus(post?.status || 'draft');
    setPublishedAt(post?.publishedAt ? toLocalInput(post.publishedAt) : toLocalInput(new Date().toISOString()));
    setDateMode('datetime');
    setEditorHtml(post?.content || '');
    setTitleManuallyEdited(false);
  }, [post?.id]);

  const initialConfig = {
    namespace: 'blog-editor',
    theme,
    onError,
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode],
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!titleManuallyEdited) {
      setSlug(generateSlug(value));
    }
  };

  const handleSlugChange = (value: string) => {
    setSlug(value);
    setTitleManuallyEdited(true);
  };

  const handleCoverImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset immediately so re-selecting the same file still fires a change event.
    e.target.value = '';
    if (!file) return;

    setCoverUploading(true);
    try {
      // Shrink the image before it is embedded in the saved post so a large
      // photo can never push the post past the browser's storage limit.
      const dataUrl = await compressImageToDataUrl(file);
      setCoverImage(dataUrl);
    } catch {
      alert('That image could not be read. Please try another file or paste an image URL.');
    } finally {
      setCoverUploading(false);
    }
  };

  const handleDocUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.name.endsWith('.txt')) {
      const reader = new FileReader();
      reader.onload = () => {
        const text = reader.result as string;
        const htmlContent = text
          .split('\n')
          .map((line) => (line.trim() ? `<p>${line.trim()}</p>` : ''))
          .join('');
        setEditorHtml((prev) => prev + (prev ? '\n' : '') + htmlContent);
      };
      reader.readAsText(file);
    } else if (file.name.endsWith('.docx')) {
      import('mammoth').then((mammoth) => {
        const reader = new FileReader();
        reader.onload = () => {
          const arrayBuffer = reader.result as ArrayBuffer;
          mammoth
            .convertToHtml({ arrayBuffer })
            .then((result: { value: string }) => {
              setEditorHtml((prev) => prev + (prev ? '\n' : '') + result.value);
            })
            .catch(() => {
              alert('Failed to parse the Word document. Please try copying and pasting the text directly.');
            });
        };
        reader.readAsArrayBuffer(file);
      }).catch(() => {
        alert('Word document parser is loading. Please try again in a moment.');
      });
    } else if (file.name.endsWith('.doc')) {
      alert('Legacy .doc format is not supported. Please save your file as .docx or .txt, or copy and paste the text directly.');
    } else {
      alert('Please upload a .txt or .docx file.');
    }

    if (docInputRef.current) {
      docInputRef.current.value = '';
    }
  };

  const handleChange = useCallback((editorState: EditorState, editor: LexicalEditor) => {
    editorState.read(() => {
      const html = $generateHtmlFromNodes(editor);
      setEditorHtml(html);
    });
  }, []);

  const tags = tagsInput
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

  const publishedDatePart = publishedAt ? publishedAt.slice(0, 10) : '';
  const publishedTimePart = publishedAt && publishedAt.length >= 16 ? publishedAt.slice(11, 16) : '';
  const todayValue = toLocalInput(new Date().toISOString()).slice(0, 10);

  const handleSave = (newStatus: 'draft' | 'published') => {
    if (!title.trim()) {
      alert('Please enter a title.');
      return;
    }
    const finalSlug = slug || generateSlug(title);
    onSave({
      title: title.trim(),
      slug: finalSlug,
      excerpt: excerpt.trim(),
      coverImage,
      content: editorHtml,
      category: category.trim(),
      tags,
      status: newStatus,
      publishedAt: fromLocalInput(publishedAt) || new Date().toISOString(),
    });
  };

  const handlePreview = () => {
    const previewSlug = slug || generateSlug(title);
    onPreview(previewSlug);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-foreground-700 mb-1.5">
          Title <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="Enter post title..."
          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 text-foreground-900 placeholder:text-foreground-400"
        />
      </div>

      {/* Slug */}
      <div>
        <label className="block text-sm font-medium text-foreground-700 mb-1.5">Slug</label>
        <div className="flex items-center gap-2">
          <span className="text-xs text-foreground-400 whitespace-nowrap">/blog/</span>
          <input
            type="text"
            value={slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            placeholder="post-url-slug"
            className="flex-1 px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 text-foreground-900 placeholder:text-foreground-400"
          />
        </div>
      </div>

      {/* Excerpt */}
      <div>
        <label className="block text-sm font-medium text-foreground-700 mb-1.5">Excerpt</label>
        <textarea
          value={excerpt}
          onChange={(e) => setExcerpt(e.target.value)}
          placeholder="A short summary of the post..."
          rows={2}
          maxLength={300}
          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 text-foreground-900 placeholder:text-foreground-400 resize-none"
        />
        <p className="text-xs text-foreground-400 mt-1">{excerpt.length}/300</p>
      </div>

      {/* Category & Tags Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">Category</label>
          <input
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="e.g. Nervous System, NLP..."
            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 text-foreground-900 placeholder:text-foreground-400"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">Tags (comma separated)</label>
          <input
            type="text"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="e.g. healing, boundaries, NLP"
            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 text-foreground-900 placeholder:text-foreground-400"
          />
        </div>
      </div>

      {/* Publish Date */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
          <label className="block text-sm font-medium text-foreground-700">
            Publish Date
          </label>
          <div className="inline-flex items-center bg-background-100 rounded-md p-1">
            <button
              onClick={() => setDateMode('datetime')}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors cursor-pointer whitespace-nowrap ${
                dateMode === 'datetime'
                  ? 'bg-background-50 text-foreground-900 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
              type="button"
            >
              Date & time
            </button>
            <button
              onClick={() => setDateMode('date')}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors cursor-pointer whitespace-nowrap ${
                dateMode === 'date'
                  ? 'bg-background-50 text-foreground-900 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
              type="button"
            >
              Date only
            </button>
          </div>
        </div>

        {dateMode === 'datetime' ? (
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="datetime-local"
              value={publishedAt}
              onChange={(e) => setPublishedAt(e.target.value)}
              className="px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 text-foreground-900"
            />
            <button
              onClick={() => setPublishedAt(toLocalInput(new Date().toISOString()))}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-background-100 text-foreground-700 rounded-md hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              type="button"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-time-line text-xs" />
              </span>
              Set to now
            </button>
            <span className="text-xs text-foreground-400">
              Shown publicly on the post. Set a past date to backdate, or a future date to schedule.
            </span>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="date"
              value={publishedDatePart}
              onChange={(e) => setPublishedAt(combineDateAndTime(e.target.value, publishedTimePart))}
              className="px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 text-foreground-900"
            />
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-foreground-500 whitespace-nowrap">Time (optional)</span>
              <input
                type="time"
                value={publishedTimePart}
                onChange={(e) => setPublishedAt(combineDateAndTime(publishedDatePart, e.target.value))}
                className="px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 text-foreground-900"
              />
            </div>
            <button
              onClick={() =>
                setPublishedAt(combineDateAndTime(todayValue, publishedTimePart || '12:00'))
              }
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-background-100 text-foreground-700 rounded-md hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              type="button"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-calendar-line text-xs" />
              </span>
              Today
            </button>
            <span className="text-xs text-foreground-400">
              Ideal for publishing several posts on the same day — leave the time blank to default to midday.
            </span>
          </div>
        )}
      </div>

      {/* Cover Image */}
      <div>
        <label className="block text-sm font-medium text-foreground-700 mb-1.5">Cover Image</label>
        <div className="flex flex-col gap-3">
          {coverImage && (
            <div className="relative w-full h-48 rounded-lg overflow-hidden border border-background-200">
              <img src={coverImage} alt="Cover preview" className="w-full h-full object-cover" />
              <button
                onClick={() => setCoverImage('')}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors cursor-pointer"
                type="button"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-close-line text-sm" />
                </span>
              </button>
            </div>
          )}
          <div className="flex items-center gap-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={coverUploading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-background-100 text-foreground-700 rounded-md hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-wait"
              type="button"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={coverUploading ? 'ri-loader-4-line text-sm animate-spin' : 'ri-image-add-line text-sm'} />
              </span>
              {coverUploading ? 'Optimising…' : coverImage ? 'Change Image' : 'Upload Cover Image'}
            </button>
            <span className="text-xs text-foreground-400">or paste an image URL below</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleCoverImageUpload}
              className="hidden"
            />
          </div>
          <input
            type="text"
            value={coverImage}
            onChange={(e) => setCoverImage(e.target.value)}
            placeholder="https://example.com/cover-image.jpg"
            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 text-foreground-900 placeholder:text-foreground-400"
          />
        </div>
      </div>

      {/* Import Word Document */}
      <div>
        <label className="block text-sm font-medium text-foreground-700 mb-1.5">
          Import from Word Document
        </label>
        <div className="flex items-center gap-3">
          <button
            onClick={() => docInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-background-100 text-foreground-700 rounded-md hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            type="button"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-file-word-2-line text-sm" />
            </span>
            Upload .docx or .txt
          </button>
          <span className="text-xs text-foreground-400">
            Upload a Word document to import its content as text (not HTML)
          </span>
          <input
            ref={docInputRef}
            type="file"
            accept=".docx,.txt"
            onChange={handleDocUpload}
            className="hidden"
          />
        </div>
      </div>

      {/* Rich Text Editor */}
      <div>
        <label className="block text-sm font-medium text-foreground-700 mb-1.5">Content</label>
        <div className="border border-background-200 rounded-lg overflow-hidden">
          <LexicalComposer initialConfig={initialConfig}>
            <div className="editor-container">
              <ToolbarPlugin />
              <div className="relative min-h-[300px]">
                <RichTextPlugin
                  contentEditable={
                    <ContentEditable className="blog-content min-h-[300px] px-5 py-4 focus:outline-none" />
                  }
                  placeholder={
                    <div className="absolute top-4 left-5 text-sm text-foreground-400 pointer-events-none select-none">
                      Start writing your post... or use the toolbar to format your text.
                    </div>
                  }
                  ErrorBoundary={LexicalErrorBoundary}
                />
              </div>
              <HistoryPlugin />
              <OnChangePlugin onChange={handleChange} />
              <UpdateEditorPlugin initialHtml={editorHtml} postId={post?.id} />
            </div>
          </LexicalComposer>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-background-200/70">
        <div className="flex items-center gap-2">
          <span className="text-xs text-foreground-500">
            Status: <span className={`font-semibold ${status === 'published' ? 'text-green-600' : 'text-foreground-500'}`}>{status === 'published' ? 'Published' : 'Draft'}</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handlePreview}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-background-100 text-foreground-700 rounded-md hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            type="button"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-eye-line text-sm" />
            </span>
            Preview
          </button>
          <button
            onClick={() => handleSave('draft')}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-background-100 text-foreground-700 rounded-md hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            type="button"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-save-line text-sm" />
            </span>
            Save Draft
          </button>
          <button
            onClick={() => handleSave('published')}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            type="button"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-send-plane-line text-sm" />
            </span>
            {status === 'published' ? 'Update & Publish' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
}